// Single app-wide TanStack Query client (0.7). Wrapped in main.tsx so every
// route (shell + Login) shares the same cache + dedupes parallel fetches.
//
// Defaults chosen for a workspace shell where most data is engagement-scoped
// and changes through user actions (not real-time pushes):
//   - `staleTime: 30s` — avoid hammering the backend on quick re-mounts while
//     keeping the freshness window short enough that the chrome reflects edits
//     within a normal interaction beat.
//   - `gcTime: 5min` — keep recently-used queries warm across route hops so
//     navigating engagement → project → back doesn't re-fetch the top-level data.
//   - `retry: 1` — one transient retry; further failures surface to the UI
//     immediately (the shell renders explicit error states, not silent retries).
//   - `refetchOnWindowFocus: false` — the shell already runs cross-tab presence
//     via @perlab/cross-tab; window-focus refetches would double-trigger on
//     every tab switch.
import { QueryClient } from "@tanstack/react-query";

// Keep visited pages warm across normal workspace work, without extending the freshness window.
// Memory-only: AuthProvider clears these entries before logout or a different actor can render.
export const SCOPED_PAGE_STALE_MS = 30_000;
export const SCOPED_PAGE_GC_MS = 30 * 60_000;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/** Revalidate cached page reads after a write; page-owned refreshes may defer active refetches. */
export function invalidateScopedPages(refetchType: "active" | "none" = "active"): void {
  void queryClient.invalidateQueries({ queryKey: ["scoped-page"], refetchType });
}

/** A deleted scope must lose its cached rows immediately, including cached ancestor summaries. */
export function resetScopedPages(): void {
  void queryClient.resetQueries({ queryKey: ["scoped-page"] });
}
